// src/models/PreferenceModel.js

export class PreferenceModel {
  static ALLOWED_KEYS = [
    "id",
    "preferenceId",
    "companyId",
    "preferenceType",
    "preferenceName",
    "preferenceValue",
    "deleted",
    "userId",

    // Scope
    "scope",

    // Audit
    "metadata",
  ];

  static ALLOWED_SCOPES = ["system", "company", "user"];

  static ALLOWED_METADATA_KEYS = [
    "createdAt",
    "updatedAt",
    "deletedFlag",
    "createdBy",
    "modifiedBy",
  ];

  constructor(data = {}, { isCreation = false } = {}) {
    // Initialise every allowed key to null so toPayload is deterministic.
    for (const key of PreferenceModel.ALLOWED_KEYS) {
      this[key] = null;
    }

    if (isCreation) {
      // ── Identity ──────────────────────────────────────────────────
      this.id = data.id || PreferenceModel._buildDocId();

      this.preferenceId =
        data.preferenceId ||
        PreferenceModel._buildPreferenceId(
          data.preferenceValue ?? data.preferenceName
        );

      // ── Tenancy ───────────────────────────────────────────────────
      this.companyId = data.companyId || "";
      this.userId = data.userId || null;

      // ── Scope ─────────────────────────────────────────────────────
      this.scope = PreferenceModel.ALLOWED_SCOPES.includes(data.scope)
        ? data.scope
        : "company";

      // ── Values ────────────────────────────────────────────────────
      this.preferenceType = data.preferenceType || "";
      this.preferenceName = data.preferenceName || "";
      this.preferenceValue = this._ensureString(data.preferenceValue);
      this.deleted = data.deleted !== undefined ? data.deleted : false;

      // ── Metadata ──────────────────────────────────────────────────
      const now = new Date().toISOString();
      this.metadata = {
        createdAt: data.metadata?.createdAt || now,
        updatedAt: data.metadata?.updatedAt || now,
        deletedFlag:
          data.metadata?.deletedFlag !== undefined
            ? data.metadata.deletedFlag
            : false,
        createdBy: data.metadata?.createdBy || this.id || null,
        modifiedBy: data.metadata?.modifiedBy || this.id || null,
      };
    } else {
      // ── Update / partial-merge mode ───────────────────────────────
      for (const key of Object.keys(data)) {
        if (!PreferenceModel.ALLOWED_KEYS.includes(key)) continue;

        if (key === "preferenceValue") {
          this[key] = this._ensureString(data[key]);
        } else if (key === "metadata") {
          if (data.metadata && typeof data.metadata === "object") {
            for (const metaKey of Object.keys(data.metadata)) {
              if (!PreferenceModel.ALLOWED_METADATA_KEYS.includes(metaKey)) {
                throw new Error(
                  `Invalid metadata property "${metaKey}" not allowed.`
                );
              }
            }
            this.metadata = { ...(this.metadata || {}), ...data.metadata };
          }
        } else {
          this[key] = data[key];
        }
      }
    }
  }

  // ── ID builders ───────────────────────────────────────────────────
  static _buildDocId() {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }
    return `pref-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  static _buildPreferenceId(preferenceValue) {
    const slugify = (str) => {
      if (!str && str !== 0) return "";
      return String(str)
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/[\s]+/g, "-")
        .replace(/-{2,}/g, "-")
        .replace(/^-+|-+$/g, "");
    };

    const slug = slugify(preferenceValue);
    if (slug) return slug;
    return `pref-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  // ── Helpers ────────────────────────────────────────────────────────
  _ensureString(value) {
    if (value === null || value === undefined) return "";
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean")
      return String(value);
    if (typeof value === "object") {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  }

  // ── Validation ─────────────────────────────────────────────────────
  isValid() {
    if (!this.id) return false;
    if (!this.preferenceId) return false;
    if (!this.companyId) return false;
    if (!this.preferenceType) return false;
    if (!this.preferenceName) return false;
    if (typeof this.preferenceValue !== "string") return false;
    if (this.preferenceValue.trim() === "") return false;

    if (
      this.scope !== undefined &&
      this.scope !== null &&
      !PreferenceModel.ALLOWED_SCOPES.includes(this.scope)
    ) {
      return false;
    }

    if (this.scope === "user" && !this.userId) return false;

    return true;
  }

  // ── Payload ────────────────────────────────────────────────────────
  toPayload() {
    const payload = {};
    for (const key of PreferenceModel.ALLOWED_KEYS) {
      if (this[key] === undefined) continue;

      if (key === "preferenceValue") {
        payload[key] = this._ensureString(this[key]);
      } else if (key === "metadata") {
        // Safe check: ensure this.metadata is truthy and an object
        if (this.metadata && typeof this.metadata === "object") {
          const metaPayload = {};
          for (const metaKey of PreferenceModel.ALLOWED_METADATA_KEYS) {
            if (this.metadata[metaKey] !== undefined) {
              metaPayload[metaKey] = this.metadata[metaKey];
            }
          }
          if (Object.keys(metaPayload).length > 0) {
            payload.metadata = metaPayload;
          }
        }
      } else {
        payload[key] = this[key] ?? null;
      }
    }
    return payload;
  }
}

export default PreferenceModel;